import axiosInstance from "./api";

const isValidMongoId = (id) => /^[a-f\d]{24}$/i.test(String(id || ""));

const mapPaymentPlan = (proposalData = {}) => {
  const rawPlan = String(
    proposalData.paymentPlan || proposalData.plan || "flexible",
  )
    .toLowerCase()
    .trim();

  if (rawPlan.includes("direct") || rawPlan === "flexible plan") return "direct";
  if (rawPlan.includes("moderate")) return "moderate";
  if (rawPlan.includes("flexible")) return "flexible";
  if (rawPlan.includes("secure")) return "secure";
  if (rawPlan.includes("crew")) return "crew";
  return "flexible";
};

// Send a Proposal
export const sendProposal = async (eventId, proposalData) => {
  try {
    const resolvedCustomerId =
      proposalData?.customerId ||
      proposalData?.customer?._id ||
      proposalData?.customer ||
      null;

    const numericQuote =
      typeof proposalData.quote === "string"
        ? Number(proposalData.quote)
        : typeof proposalData.budget === "string"
          ? Number(proposalData.budget)
          : (proposalData.quote ?? proposalData.budget);

    const normalizedPlan = mapPaymentPlan(proposalData);
    const normalizedDetail =
      proposalData.proposalDetail || proposalData.description || "";
    const normalizedServices = Array.isArray(proposalData.services)
      ? proposalData.services
      : Array.isArray(proposalData.servicesBreakdown)
        ? proposalData.servicesBreakdown
        : [];

    const response = await axiosInstance.post(`/proposals/send/${eventId}`, {
      eventId,
      quote: numericQuote,
      plan: normalizedPlan,
      proposalDetail: normalizedDetail,
      services: normalizedServices,
      proposedBudget:
        typeof numericQuote === "number" ? numericQuote : Number(numericQuote),
      currency: proposalData.currency || "INR",
      proposalDescription: normalizedDetail,
      servicesBreakdown: Array.isArray(proposalData.servicesBreakdown)
        ? proposalData.servicesBreakdown
        : [],
      additionalNotes: proposalData.additionalNotes || proposalData.notes || "",
      ...(proposalData.eventModel
        ? { eventModel: proposalData.eventModel }
        : {}),
      ...(resolvedCustomerId ? { customerId: resolvedCustomerId } : {}),
      paymentPlan: normalizedPlan,
    });

    console.log("[sendProposal] response.data:", response.data);

    return response.data;
  } catch (error) {
    console.error("Send Proposal Error:", error);
    throw error?.response?.data || { message: "Failed to send proposal" };
  }
};

export const getProposals = async () => {
  try {
    const res = await axiosInstance.get("/proposals/customer");
    return res?.data?.proposals || [];
  } catch (error) {
    console.error("Get Proposals Error:", error);
    throw error?.response?.data || { message: "Failed to fetch proposals" };
  }
};

export const getCustomerProposalsByRequirement = async (requirementId) => {
  try {
    if (!requirementId || !isValidMongoId(requirementId)) {
      throw new Error("Valid requirementId is required");
    }

    const res = await axiosInstance.get(
      `/proposals/customer/requirement/${requirementId}`,
    );

    return res?.data?.proposals || [];
  } catch (error) {
    console.error("Get Requirement Proposals Error:", error);
    throw (
      error?.response?.data || {
        message: "Failed to fetch requirement proposals",
      }
    );
  }
};

export const getProposalById = async (proposalId) => {
  try {
    if (!proposalId || !isValidMongoId(proposalId)) {
      throw new Error("Valid proposalId is required");
    }

    const response = await axiosInstance.get(`/proposals/${proposalId}`);
    return response.data;
  } catch (error) {
    console.error("Get Proposal By ID Error:", error);
    throw error?.response?.data || { message: "Failed to fetch proposal" };
  }
};

// Fetch all proposals sent by this user
export const getSentProposals = async () => {
  try {
    const res = await axiosInstance.get("/proposals/sent");
    return res?.data?.proposals || [];
  } catch (error) {
    console.error("Get Sent Proposals Error:", error);
    throw error?.response?.data || { message: "Failed to fetch proposals" };
  }
};

// Edit an existing proposal
export const editProposal = async (proposalId, data) => {
  try {
    const numericQuote =
      typeof data.quote === "string"
        ? Number(data.quote)
        : typeof data.budget === "string"
          ? Number(data.budget)
          : (data.quote ?? data.budget);

    const normalizedPlan = mapPaymentPlan(data);
    const normalizedDetail = data.proposalDetail || data.description || "";
    const normalizedServices = Array.isArray(data.services)
      ? data.services
      : Array.isArray(data.servicesBreakdown)
        ? data.servicesBreakdown
        : [];

    const res = await axiosInstance.put(`/proposals/${proposalId}`, {
      quote: numericQuote,
      plan: normalizedPlan,
      proposalDetail: normalizedDetail,
      services: normalizedServices,
      proposedBudget:
        typeof numericQuote === "number" ? numericQuote : Number(numericQuote),
      currency: data.currency || "INR",
      proposalDescription: normalizedDetail,
      servicesBreakdown: Array.isArray(data.servicesBreakdown)
        ? data.servicesBreakdown
        : [],
      additionalNotes: data.additionalNotes || data.notes || "",
      paymentPlan: normalizedPlan,
    });

    console.log("[editProposal] response.data:", res.data);

    return res?.data?.proposal;
  } catch (error) {
    console.error("Edit Proposal Error:", error);
    throw error?.response?.data || { message: "Failed to edit proposal" };
  }
};

// Update proposal status
export const updateProposalStatus = async (proposalId, status) => {
  try {
    const res = await axiosInstance.patch(`/proposals/${proposalId}/status`, {
      status,
    });
    return res.data;
  } catch (error) {
    console.error("Update Proposal Status Error:", error);
    throw error?.response?.data || { message: "Failed to update status" };
  }
};

// Withdraw a proposal (professional only — permanent, cannot re-submit)
export const withdrawProposal = async (proposalId) => {
  try {
    const res = await axiosInstance.patch(`/proposals/${proposalId}/withdraw`);
    return res.data;
  } catch (error) {
    console.error("Withdraw Proposal Error:", error);
    throw error?.response?.data || { message: "Failed to withdraw proposal" };
  }
};

export const getSelectedProposalsSummary = async (idsString) => {
  try {
    if (!idsString) return [];
    const res = await axiosInstance.get(`/proposals/customer/selected-summary?ids=${idsString}`);
    return res?.data?.proposals || [];
  } catch (error) {
    console.error("Get Selected Proposals Summary Error:", error);
    throw error?.response?.data || { message: "Failed to fetch selected proposals summary details" };
  }
};
